<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          Export Report
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex justify-center w-full flex-col space-y-4">
        <h2 class="text-lg font-bold">
          {{ report.title }}
        </h2>

        <label>
          Month

          <AppInput
            v-model="reportMonth"
            name="month"
            type="month"
            class="form-input"
          />
        </label>

        <div
          v-if="report.issue_to?.length > 0"
          class="mt-4 p-3 rounded-md cursor-pointer"
          :class="[issueReport ? 'bg-blue-50' : 'bg-gray-50']"
          @click="toggleReport"
        >
          <div class="text-sm font-medium mb-2">
            <label
              class="flex items-center justify-between cursor-pointer"
              @click.stop=""
            >
              <span>
                Issue report to the following
              </span>

              <AppInput
                v-model="issueReport"
                name="issue_report"
                type="checkbox"
                class="form-checkbox"
              />
            </label>
          </div>

          <ul
            class="text-sm space-y-1"
            :class="[issueReport ? 'text-blue-800' : 'text-gray-800']"
          >
            <li
              v-for="recipient in report.issue_to"
              :key="recipient.email"
            >
              {{ recipient.user_details?.name || recipient.email }}
              <span
                v-if="recipient.user_details?.name"
                class="text-blue-600"
              >
                ({{ recipient.email }})
              </span>
            </li>
          </ul>
        </div>

        <div
          v-else
          class="mt-4 p-3 bg-yellow-50 rounded-md"
        >
          <div class="text-sm text-yellow-800">
            No recipients configured for this report. <br> The report will only be generated and sent to you only.
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <button
          type="button"
          class="btn btn--tertiary-blue"
          @click.prevent="$emit('close')"
        >
          Close
        </button>

        <button
          type="button"
          class="btn btn--primary-blue flex items-center justify-center space-x-2 ml-2"
          :disabled="generateReportAction.is(States.LOADING)"
          @click="generateReport"
        >
          <svg
    fill="none"
    viewBox="0 0 24 24"
 v-if="generateReportAction.is(States.LOADING)"
           
            class="animate-spin h-5 w-5 text-white">
    <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
    />

    <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
</svg>

          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" v-else
           
            class="w-5 h-5">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

          <span>
            Generate Report
          </span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForMonthInput } from '@/helpers';
import ReportService from '@/services/ReportService';
import { useReportsStore } from '@/store/reports';
import Swal from 'sweetalert2';
import { ref } from 'vue';

interface Props {
  report: {
    id: number;
    title: string;
    issue_to?: {
      email: string;
      user?: number;
      user_details?: {
        id: number;
        name: string;
        email: string;
      };
    }[];
  };
}

const props = defineProps<Props>();

const emits = defineEmits<{
  (e: 'close');
}>();

const reportsStore = useReportsStore();
const reportMonth = ref(dateForMonthInput(new Date()));
const issueReport = ref(false);
const generateReportAction = useStoreApiAction(reportsStore.generate);

const toggleReport = () => {
  issueReport.value = !issueReport.value;
};

const generateReport = () => {
  generateReportAction.request(props.report.id, { month: reportMonth.value, issue_report: issueReport.value }).then(
    (data) => {
      const hasRecipients = props.report.issue_to && props.report.issue_to.length > 0;

      const message = hasRecipients && issueReport.value ?
        'The report is being generated and will be sent to all configured recipients when ready.' :
        'The report is being generated and will be emailed to you when ready.';

      Swal.fire({
        icon: 'success',
        title: 'Report Generating',
        text: message,
      }).then(() => {
        emits('close');
      });
    },
  ).catch((error) => {
    if(error.status === 429) {
      Swal.fire({
        icon: 'error',
        title: 'PDF is currently being exported',
        text: error.data.message,
      });
    } else if(error.status === 422) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Export.',
        text: error.data.message,
      });
    }
  });
};
</script>

<style scoped></style>
